import { createContext, useContext, useEffect, useState } from 'react'
import useWindowDimensions from '../hooks/Dimensions'
import { ThemeProvider } from './ThemeContext'
import { api } from '../services/api'
import { FontSizeProvider } from './FontSizeProvider'
import { TENANTS, TenantConfig, DEFAULT_TENANT } from '../config/tenants'

interface IAppContext {
	mobile: boolean
	visible: boolean
	setVisible: React.Dispatch<React.SetStateAction<boolean>>
	menuMobile: boolean
	inputSearch: string
	smallScreen: boolean
	setInputSearch: React.Dispatch<React.SetStateAction<string>>
	configDomainURL: string
	basename: string
	configApiURL: string
	tenant: TenantConfig
	getTenantUrl: (path: string) => string
}

const AppContext = createContext({} as IAppContext)

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
	const getBaseUrl = () => {
		if (window.location.hostname === 'appdev') {
			return 'http://appdev/portais/portal-municipio/deploy/'
		}
		return window.location.origin + '/'
	}

	const [basename, setBasename] = useState('')
	const [configDomainURL, setConfigDomainURL] = useState(getBaseUrl())
	const [configApiURL, setConfigApiURL] = useState('')
	const [loading, setLoading] = useState(true)
	const [mobile, setMobile] = useState<boolean>(false)
	const [menuMobile, setMenuMobile] = useState<boolean>(false)
	const [smallScreen, setSmallScreen] = useState<boolean>(false)
	const [inputSearch, setInputSearch] = useState<string>('')
	const { width } = useWindowDimensions()
	const [visible, setVisible] = useState<boolean>(false)
	const [tenant, setTenant] = useState<TenantConfig>(DEFAULT_TENANT)

	const tamanho_mobile = 1024

	useEffect(() => {
		if (tamanho_mobile > width) {
			setMobile(true)
		} else {
			setMobile(false)
		}
	}, [width])

	useEffect(() => {
		if (tamanho_mobile <= width) {
			setSmallScreen(true)
		} else {
			setSmallScreen(false)
		}
	}, [width])

	useEffect(() => {
		if (tamanho_mobile > width) {
			setMenuMobile(true)
		} else {
			setMenuMobile(false)
		}
	}, [width])

	const getTenantUrl = (url: string) => {
		const hostname = window.location.hostname
		const isDev = hostname === 'localhost' || hostname === 'appdev'

		if (!isDev || !url) return url

		// If URL is absolute pointing to one of our production domains, map it back to localhost + ?tenant=ID
		const sortedTenants = Object.entries(TENANTS).sort((a, b) => b[0].length - a[0].length)

		for (const [domain, config] of sortedTenants) {
			const httpDomain = `http://${domain}`
			const httpsDomain = `https://${domain}`

			if (url.startsWith(httpDomain) || url.startsWith(httpsDomain)) {
				// Prevent matching things like api-portal.itajai... as if they were the main portal
				if (url.includes('api-') || url.includes('adm-')) continue

				const relativePath = url.includes(domain) ? url.split(domain)[1] || '/' : url
				const connector = relativePath.includes('?') ? '&' : '?'
				return `${relativePath}${connector}tenant=${config.id}`
			}
		}

		// Handle localhost or appdev absolute URLs
		const currentOrigin = window.location.origin
		let processedUrl = url
		if (url.startsWith(currentOrigin)) {
			processedUrl = url.replace(currentOrigin, '')
		}

		// For relative paths or URLs that were part of current origin, append current tenant if missing
		if (processedUrl.startsWith('/') || !processedUrl.includes('://')) {
			const searchParams = new URLSearchParams(window.location.search)
			const currentTenant = searchParams.get('tenant')
			if (currentTenant && !processedUrl.includes('tenant=')) {
				const connector = processedUrl.includes('?') ? '&' : '?'
				return `${processedUrl}${connector}tenant=${currentTenant}`
			}
			return processedUrl
		}

		return url
	}

	useEffect(() => {
		; (async () => {
			try {
				// Detect Tenant
				const hostname = window.location.hostname
				const searchParams = new URLSearchParams(window.location.search)
				const tenantOverride = searchParams.get('tenant')

				let selectedTenant = TENANTS[hostname] || DEFAULT_TENANT

				// Development override via ?tenant=id
				if (hostname === 'localhost' || hostname === 'appdev') {
					if (tenantOverride) {
						const found = Object.values(TENANTS).find(t => t.id === tenantOverride)
						if (found) selectedTenant = found
					}
				}

				setTenant(selectedTenant)

				const response = await fetch(
					`${getBaseUrl()}ini.json`,
					{
						headers: {
							Accept: 'application/json',
						},
					}
				)

				if (!response.ok) {
					throw new Error(`Failed to load ini.json: ${response.status} ${response.statusText}`)
				}

				const configJson = await response.json()

				// Ensure api_url ends with slash if it doesn't already
				const baseApi = configJson.api_url.endsWith('/') ? configJson.api_url : `${configJson.api_url}/`

				// Set dynamic API Base URL
				api.defaults.baseURL = `${baseApi}${selectedTenant.apiSuffix}/api/`

				setConfigApiURL(configJson.api_url)
				setConfigDomainURL(getBaseUrl())
				setBasename(configJson.basename || '')
			} catch (error) {
				console.error("Failed to load configuration:", error)
			} finally {
				setLoading(false)
			}
		})()
	}, [])

	if (loading) {
		return null
	}

	return (
		<ThemeProvider>
			<FontSizeProvider>
				<AppContext.Provider
					value={{
						mobile,
						menuMobile,
						smallScreen,
						setVisible,
						visible,
						inputSearch,
						setInputSearch,
						configDomainURL,
						basename,
						configApiURL,
						tenant,
						getTenantUrl
					}}
				>
					{children}
				</AppContext.Provider>
			</FontSizeProvider>
		</ThemeProvider>
	)
}

export function useApp() {
	const context = useContext(AppContext)
	return context
}
